import { NumberOptions, Static, StringOptions, Type } from '@sinclair/typebox';

export const PaginationLimitParamsSchema = (options?: NumberOptions) => {
  return Type.Number({
    description:
      'The limit of items to be returned. The maximum allowed value depends on the provider. This is a ceiling, ' +
      'not a guarantee: some providers can return fewer items than requested for a given page.',
    default: 20,
    minimum: 1,
    ...options,
  });
};

export const PaginationOffsetParamsSchema = (options?: NumberOptions) => {
  return Type.Number({
    description: 'An offset used for pagination.',
    ...options,
  });
};

export const PaginationCursorParamsSchema = (options?: StringOptions) => {
  return Type.String({
    description: 'A cursor used for pagination. Use `next_cursor` given by the previous page of the list.',
    ...options,
  });
};

/**
 * This must be used when the pagination is always based on an offset. (Specifics methods)
 *
 * The limit default is set to 20, but can be overridden depending on the method.
 */
export const PaginationQueryOffsetBasedParamsSchema = (options?: { offset?: NumberOptions; limit?: NumberOptions }) => {
  return Type.Object(
    {
      offset: Type.Optional(PaginationOffsetParamsSchema(options?.offset)),
      limit: Type.Optional(PaginationLimitParamsSchema(options?.limit)),
    },
    { additionalProperties: false },
  );
};

/**
 * This must be used when the pagination is always based on a cursor. (Specifics methods)
 *
 * The limit default is set to 20, but can be overridden depending on the method.
 */
export const PaginationQueryCursorBasedParamsSchema = (options?: { cursor?: StringOptions; limit?: NumberOptions }) => {
  return Type.Object(
    {
      cursor: Type.Optional(PaginationCursorParamsSchema(options?.cursor)),
      limit: Type.Optional(PaginationLimitParamsSchema(options?.limit)),
    },
    { additionalProperties: false },
  );
};

/**
 * This must be used when the pagination can be based on an offset or a cursor, but will depend on the provider. (Common methods)
 * The validation of the chosen pagination method must be done on the methods service level.
 *
 * The limit default is set to 20. The maximum depends on the provider and is validated at runtime.
 */
export const PaginationQueryParamsSchema = Type.Object({
  offset: Type.Optional(
    PaginationOffsetParamsSchema({
      // Override the default description to be more specific
      description: 'An offset used for pagination, if supported by the provider, else use `cursor`.',
    }),
  ),
  cursor: Type.Optional(
    PaginationCursorParamsSchema({
      // Override the default description to be more specific
      description:
        'A cursor used for pagination. If supported by the provider, use `next_cursor` given by the previous page of the list, else use `offset`.',
    }),
  ),
  limit: Type.Optional(PaginationLimitParamsSchema()),
});

export type PaginationQueryOffsetBasedParams = Static<ReturnType<typeof PaginationQueryOffsetBasedParamsSchema>>;
export type PaginationQueryCursorBasedParams = Static<ReturnType<typeof PaginationQueryCursorBasedParamsSchema>>;
